import { currencyFormat, dpString, roundToCents } from "common/numbers";
import {
  TYPE_ADDITIONAL,
  TYPE_RETURNED,
} from "components/layers/ActiveLayerPremiumAdjustmentsEditor";
import { NONE_PAPER_PROVIDER } from "domain/layerSupport";
import * as _ from "lodash";

const formatFeeRate = (rate) =>
  dpString(2, { minDp: 0, suffix: "%" })(rate * 100);

export const totalNetPremium = (boundLayers) =>
  roundToCents(
    (boundLayers ?? []).reduce((sum, l) => sum + (l?.netPremium ?? 0), 0)
  );

export const totalNetAdjustments = (boundLayers) =>
  roundToCents(
    (boundLayers ?? []).reduce(
      (sum, l) =>
        sum +
        (l?.adjustments ?? []).reduce(
          (adjSum, a) => adjSum + (a?.netPremiumChange ?? 0),
          0
        ),
      0
    )
  );

export const totalNetDue = (boundLayers) =>
  roundToCents(totalNetPremium(boundLayers) + totalNetAdjustments(boundLayers));

export const totalReceived = (paymentsMade) =>
  roundToCents(
    (paymentsMade ?? [])
      .filter((p) => p?.deleted !== true)
      .reduce((sum, p) => sum + (p?.paymentAmount ?? 0), 0)
  );

// Splits the balance so an overpayment (e.g. after a returned premium) is shown rather than zeroed out
export const paymentBalance = (totalDue, totalPaid) => {
  const balance = roundToCents(totalDue - totalPaid);
  const isOverpaid = balance < 0;
  return {
    outstanding: isOverpaid ? 0 : balance,
    overpaid: isOverpaid ? Math.abs(balance) : 0,
  };
};

const statusMetric = (value, chipColor) => ({
  label: "Status",
  value,
  chipColor,
});

export const paymentStatusMetric = ({ totalDue, outstanding, overpaid }) => {
  if (outstanding > 0) {
    return statusMetric("Unpaid", "error");
  }
  if (overpaid > 0) {
    return statusMetric("Overpaid", "info");
  }
  return totalDue > 0
    ? statusMetric("Paid", "success")
    : statusMetric("No Charge", "default");
};

// Deductions are derived from the API's own figures so the lines always sum to the net amount shown.
export const makeBreakdown = ({
  label = "Premium",
  amount,
  netAmount,
  feeRate,
  tria,
}) => {
  if (amount == null || netAmount == null) {
    return null;
  }

  const triaAmount = tria?.amount ?? 0;
  const triaInPremium = tria?.includedInPremium ? triaAmount : 0;
  const triaOutsidePremium = tria?.includedInPremium ? 0 : triaAmount;
  const feesDeducted = roundToCents(amount + triaOutsidePremium - netAmount);

  if (!feesDeducted && !triaInPremium && !triaOutsidePremium) {
    return null;
  }

  const lines = [{ label, value: amount }];
  if (feesDeducted) {
    const feesLabel = feeRate ? `Fees (${formatFeeRate(feeRate)})` : "Fees";
    lines.push({ label: feesLabel, value: -feesDeducted });
  }
  if (triaOutsidePremium) {
    lines.push({ label: "TRIA (outside premium)", value: triaOutsidePremium });
  }

  return {
    lines,
    total: netAmount,
    note: triaInPremium
      ? `Premium includes TRIA of ${currencyFormat(triaInPremium, 2)}`
      : null,
  };
};

// The configured name of a layer's paper provider, or null when it is left as the default or set to none
export const paperProviderName = (paperProvider, reinsurers) => {
  if (paperProvider == null || paperProvider === NONE_PAPER_PROVIDER) {
    return null;
  }
  return (
    (reinsurers ?? []).find((r) => r.key === paperProvider)?.name ??
    paperProvider
  );
};

const layerDisplayName = (name) =>
  (name ?? "").replaceAll("$", "").replace(" xs ", " x ");

const isSameLayer = (a, b) => a != null && b != null && String(a) === String(b);

// Keeps the ledger's Type column to one line; the breakdown tooltip still shows the full name
const SHORT_ADJUSTMENT_TYPES = {
  [TYPE_ADDITIONAL]: "AP",
  [TYPE_RETURNED]: "RP",
};

const dueEntries = (layer) => [
  {
    id: `${layer.layerId}-premium`,
    kind: "premium",
    date: layer.dueDate || null,
    type: "Premium",
    description: layer.comment ?? "",
    due: layer.netPremium ?? 0,
    breakdown: makeBreakdown({
      amount: layer.premium,
      netAmount: layer.netPremium,
      feeRate: layer.fees?.total,
      tria: layer.tria,
    }),
  },
  ...(layer.adjustments ?? []).map((adj, idx) => ({
    id: `${layer.layerId}-adj-${idx}`,
    kind: "adjustment",
    date: adj?.dueDate || null,
    type: SHORT_ADJUSTMENT_TYPES[adj?.type] ?? adj?.type ?? "Adjustment",
    description: "",
    due: adj?.netPremiumChange ?? 0,
    breakdown: makeBreakdown({
      label: adj?.type ?? "Adjustment",
      amount: adj?.premiumChange,
      netAmount: adj?.netPremiumChange,
      feeRate: layer.fees?.total,
    }),
  })),
];

const paymentEntry = (payment) => ({
  id: payment.paymentId,
  kind: "payment",
  date: payment.paymentDate || null,
  type: (payment.paymentAmount ?? 0) < 0 ? "Refund" : "Receipt",
  description: payment.comment ?? "",
  received: payment.paymentAmount ?? 0,
  payment,
});

// On the same date the premium comes before any adjustment, and amounts fall due before they are paid
const KIND_ORDER = { premium: 0, adjustment: 1, payment: 2 };

const ledgerGroup = ({ id, layerName, dues, payments }) => {
  const entries = _.sortBy(
    [...dues, ...payments.map(paymentEntry)],
    // Dates are YYYY-MM-DD strings; undefined sorts undated entries last
    [(e) => e.date ?? undefined, (e) => KIND_ORDER[e.kind]]
  );

  let balance = 0;
  const rows = entries.map((entry) => {
    balance = roundToCents(balance + (entry.due ?? 0) - (entry.received ?? 0));
    return { ...entry, balance };
  });

  const due = roundToCents(_.sumBy(rows, (r) => r.due ?? 0));
  const received = roundToCents(_.sumBy(rows, (r) => r.received ?? 0));
  const { outstanding, overpaid } = paymentBalance(due, received);

  return {
    id,
    layerName,
    rows,
    totals: { due, received, balance: roundToCents(due - received) },
    status: paymentStatusMetric({ totalDue: due, outstanding, overpaid }),
  };
};

// One group per bound layer, in name order, holding its amounts due and payments received as a running statement.
// Payments against a layer that is no longer bound are kept in a trailing group so they don't silently disappear.
export const ledgerGroups = (boundLayers, paymentsMade) => {
  const payments = (paymentsMade ?? []).filter((p) => p?.deleted !== true);
  const layers = _.sortBy(boundLayers ?? [], (l) => l?.name ?? "");

  const groups = layers.map((layer) =>
    ledgerGroup({
      id: layer.layerId,
      layerName: layerDisplayName(layer.name),
      dues: dueEntries(layer),
      payments: payments.filter((p) => isSameLayer(p.layerId, layer.layerId)),
    })
  );

  const unmatched = payments.filter(
    (p) => !layers.some((l) => isSameLayer(p.layerId, l.layerId))
  );
  if (unmatched.length > 0) {
    groups.push({
      ...ledgerGroup({
        id: "other",
        layerName: "Other Payments",
        dues: [],
        payments: unmatched,
      }),
      // With nothing due these would always read as overpaid, which says nothing useful
      status: null,
    });
  }

  return groups;
};
